import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const VerificationSection = ({ verificationData, onUpload }) => {
  const [uploadingDoc, setUploadingDoc] = useState(null);

  const verificationSteps = [
    {
      id: 'phone',
      title: 'Phone Verification',
      description: 'Verify your phone number via SMS',
      status: verificationData?.phoneVerified ? 'completed' : 'pending',
      icon: 'Smartphone'
    },
    {
      id: 'whatsapp',
      title: 'WhatsApp Verification',
      description: 'Verify your WhatsApp number',
      status: verificationData?.whatsappVerified ? 'completed' : 'pending',
      icon: 'MessageCircle'
    },
    {
      id: 'identity',
      title: 'Identity Document',
      description: 'Upload national ID or passport',
      status: verificationData?.identityStatus,
      icon: 'FileText'
    },
    {
      id: 'photo',
      title: 'Profile Photo',
      description: 'Upload a clear photo of yourself',
      status: verificationData?.photoStatus,
      icon: 'Camera'
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return { icon: 'CheckCircle', color: 'text-success' };
      case 'pending':
        return { icon: 'Clock', color: 'text-warning' };
      case 'rejected':
        return { icon: 'XCircle', color: 'text-error' };
      default:
        return { icon: 'Circle', color: 'text-muted-foreground' };
    }
  };

  const handleDocumentUpload = async (type) => {
    setUploadingDoc(type);
    // Simulate upload process
    setTimeout(() => {
      setUploadingDoc(null);
      if (onUpload) {
        onUpload(type);
      }
    }, 2000);
  };

  const completedSteps = verificationSteps?.filter(step => step?.status === 'completed')?.length;
  const progressPercentage = (completedSteps / verificationSteps?.length) * 100;

  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-soft">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Identity Verification</h2>
          <p className="text-muted-foreground mt-1">
            Complete verification to build trust and unlock premium features
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-primary">{completedSteps}/4</div>
          <div className="text-sm text-muted-foreground">Completed</div>
        </div>
      </div>
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Verification Progress</span>
          <span className="text-sm text-muted-foreground">{Math.round(progressPercentage)}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
      </div>
      {/* Verification Steps */}
      <div className="space-y-4">
        {verificationSteps?.map((step) => {
          const statusIcon = getStatusIcon(step?.status);
          
          return (
            <div key={step?.id} className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-smooth">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                  <Icon name={step?.icon} size={20} className="text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-medium text-foreground">{step?.title}</h3>
                  <p className="text-sm text-muted-foreground">{step?.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Icon name={statusIcon?.icon} size={16} className={statusIcon?.color} />
                  <span className={`text-sm font-medium capitalize ${statusIcon?.color}`}>
                    {step?.status}
                  </span>
                </div>

                {step?.status === 'pending' && (step?.id === 'identity' || step?.id === 'photo') && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDocumentUpload(step?.id)}
                    loading={uploadingDoc === step?.id}
                    iconName="Upload"
                    iconPosition="left"
                    iconSize={14}
                  >
                    Upload
                  </Button>
                )}

                {step?.status === 'rejected' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDocumentUpload(step?.id)}
                    iconName="RefreshCw"
                    iconPosition="left"
                    iconSize={14}
                  >
                    Retry
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Uploaded Documents */}
      {(verificationData?.identityDocument || verificationData?.profilePhoto) && (
        <div className="mt-6 pt-6 border-t border-border">
          <h3 className="font-medium text-foreground mb-4">Uploaded Documents</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {verificationData?.identityDocument && (
              <div className="border border-border rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Icon name="FileText" size={16} className="text-primary" />
                  <span className="font-medium text-foreground">Identity Document</span>
                </div>
                <div className="w-full h-32 bg-muted rounded-lg overflow-hidden">
                  <Image
                    src={verificationData?.identityDocument}
                    alt="Identity document showing official government ID card"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Status: {verificationData?.identityStatus}
                </p>
              </div>
            )}

            {verificationData?.profilePhoto && (
              <div className="border border-border rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Icon name="Camera" size={16} className="text-primary" />
                  <span className="font-medium text-foreground">Profile Photo</span>
                </div>
                <div className="w-full h-32 bg-muted rounded-lg overflow-hidden">
                  <Image
                    src={verificationData?.profilePhoto}
                    alt="Clear profile photo of user for identity verification"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Status: {verificationData?.photoStatus}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Benefits */}
      {completedSteps === verificationSteps?.length && (
        <div className="mt-6 p-4 bg-success/10 border border-success/20 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Award" size={20} className="text-success mt-0.5" />
            <div>
              <h4 className="font-medium text-success">Verification Complete!</h4>
              <p className="text-sm text-success/80 mt-1">
                You now have access to premium features, professional seller badge, and increased buyer trust.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationSection;